import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { PartyPopper, Trash2 } from "lucide-react";

export default function EventsPage() {
  const { user } = useAuth();
  const canPost = ["master_admin", "super_admin", "principal", "hr_manager", "coordinator"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ title: "", date: new Date().toISOString().slice(0, 10), description: "" });
  const today = new Date().toISOString().slice(0, 10);
  const load = () => api.get("/events").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  async function save() {
    try { await api.post("/events", f); toast.success("Event added"); setF({ ...f, title: "", description: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  const upcoming = rows.filter((e) => e.date >= today);
  const past = rows.filter((e) => e.date < today).reverse();

  const Card = ({ e }) => {
    const d = new Date(`${e.date}T00:00:00`);
    return (
      <div data-testid={`event-${e.id}`} className="bg-white rounded-2xl p-4 shadow-sm flex gap-4">
        <div className="h-16 w-16 rounded-2xl text-white flex flex-col items-center justify-center shrink-0 brand-header">
          <div className="text-[10px] uppercase font-semibold">{d.toLocaleString("en-IN", { month: "short" })}</div>
          <div className="font-display text-2xl font-extrabold leading-none">{d.getDate()}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-slate-900">{e.title}</div>
          {e.description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{e.description}</p>}
          <div className="text-xs text-slate-500 mt-1">{e.branch_id ? "Branch event" : "All branches"} • {e.posted_by_name}</div>
        </div>
        {canPost && <button data-testid={`delete-event-${e.id}`} onClick={async () => { await api.delete(`/events/${e.id}`); load(); }} className="text-slate-400 hover:text-red-600 self-start"><Trash2 size={16} /></button>}
      </div>
    );
  };

  return (
    <AppShell>
      <PageHeader eyebrow="Calendar" title="Events" subtitle="Celebrations, meetings and school days." />
      {canPost && (
        <div data-testid="event-form" className="bg-white rounded-2xl shadow-sm p-5 mb-6 space-y-3">
          <h3 className="font-display font-bold text-slate-900">New event</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Title</Label><Input data-testid="event-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1" /></div>
            <div><Label>Date</Label><Input data-testid="event-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1" /></div>
          </div>
          <div><Label>Description</Label><Textarea data-testid="event-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1" /></div>
          <Button data-testid="event-save" onClick={save} disabled={!f.title} className="btn-brand">Save event</Button>
        </div>
      )}
      {rows.length === 0 ? <Empty testid="events-empty" icon={PartyPopper} text="No events planned yet." /> : (
        <div className="space-y-6">
          <section><h2 className="font-display font-bold text-slate-900 mb-3">Upcoming</h2><div className="space-y-3">{upcoming.length ? upcoming.map((e) => <Card key={e.id} e={e} />) : <p className="text-sm text-slate-500">Nothing upcoming.</p>}</div></section>
          {past.length > 0 && <section><h2 className="font-display font-bold text-slate-500 mb-3">Past</h2><div className="space-y-3 opacity-75">{past.map((e) => <Card key={e.id} e={e} />)}</div></section>}
        </div>
      )}
    </AppShell>
  );
}
